import React, { useEffect } from "react";
import { useState } from "react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import BundlesTab from "./BundlesTab";
import ProductsTab from "./ProductsTab";
import TransactionsTab from "./TransactionsTab";
import SubscriptionsTab from "@/components/dashboard/SubscriptionsTab";
import SavingsCalculatorTab from "@/components/dashboard/SavingsCalculatorTab";
import Settings from "@/pages/Settings";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import UnifiedProductForm from "@/components/products/UnifiedProductForm";
import OfferPageForm from "@/components/OfferPageForm"; // Assuming OfferPageForm is a component for creating offers
import EditOfferForm from "../editOfferForm.tsx";
import { useStripeConnection } from "@/hooks/useStripeConnection";
import { useBundleModal } from "@/contexts/BundleModalContext";
import { useProductModal } from "@/contexts/ProductModalContext";
import { RefreshButton } from "../ui/refresh-button.tsx";

interface DashboardContentProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  stripeConnected: boolean;
  hasCreatedFirstProduct: boolean;
  showSubscriptionsTab: boolean;
  isSidebarCollapsed: boolean;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  activeTab,
  setActiveTab,
  hasCreatedFirstProduct,
  showSubscriptionsTab,
  isSidebarCollapsed,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [offerFilterStatus] = useState("all");
  const [productTypeFilter] = useState("all");
  const [showTransactionFilterBar] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false); // Add state for offer modal
  const [showEditOfferModal, setShowEditOfferModal] = useState(false); // Add state for edit offer modal
  const [editingOfferPage, setEditingOfferPage] = useState(null);
  const { isConnected: isStripeConnected } = useStripeConnection();
  const { openBundleModal, closeBundleModal, showBundleModal } = useBundleModal();
  const { openProductModal, closeProductModal, showProductModal } = useProductModal();

  const [searchParams] = useSearchParams();
  const [isTransactionsRefreshing, setIsTransactionsRefreshing] = useState(false);

  const isFirstRender = React.useRef(true);

  const handleTabRefresh = React.useCallback(
    (eventName: string, setIsRefreshing: React.Dispatch<React.SetStateAction<boolean>>) => {
      setIsRefreshing(true);
      document.dispatchEvent(new CustomEvent(eventName));
      setTimeout(() => setIsRefreshing(false), 2000);
    },
    []
  );

  useEffect(() => {
    const actionParam = searchParams.get("action");
    const tabParam = searchParams.get("tab");

    if (tabParam === "products" && actionParam === "create" && activeTab === "products") {
      setShowCreateProductModal(true);
    }
  }, [searchParams, activeTab]);
  
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const actionParam = searchParams.get("action");
    
    if (activeTab === 'products') {
      setShowCreateOfferModal(false);
      setShowEditOfferModal(false);
      setEditingOfferPage(null);
      
      if (actionParam !== 'create') {
        setShowCreateProductModal(false);
      }
    } 
    else if (activeTab !== 'offers') {
      setShowCreateOfferModal(false);
      setShowCreateProductModal(false);
      setShowEditOfferModal(false);
      setEditingOfferPage(null);
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    const handleStripeDisconnected = () => {
      setSearchQuery(prev => prev + " ");
    };
    document.addEventListener("stripe-disconnected", handleStripeDisconnected);

    if (activeTab === 'bundles' && sessionStorage.getItem('openBundleModal') === 'true') {
      setTimeout(() => {
        openBundleModal();
        sessionStorage.removeItem('openBundleModal');
      }, 100);
    }
    
    if (activeTab === 'products' && sessionStorage.getItem('openProductModal') === 'true') {
      setTimeout(() => {
        openProductModal();
        sessionStorage.removeItem('openProductModal'); 
      }, 100);
    }

    if (sessionStorage.getItem('forceReloadDashboard') === 'true') {
      sessionStorage.removeItem('forceReloadDashboard');
    }

    return () => {
      document.removeEventListener("stripe-disconnected", handleStripeDisconnected);
    };
  }, [activeTab, openBundleModal, openProductModal]);

  // Handler for editing an offer
  const handleEditOffer = (offerId: string) => {
    setEditingOfferPage(offerId);
    setShowEditOfferModal(true);
  };

  // Action buttons
  const renderActionButton = () => {
    if (!isStripeConnected) return null;
    if (activeTab === "products") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="ml-2 rounded-md"
                onClick={() => {
                  openProductModal();
                }}
                aria-label="Create a new product"
              >
                <Plus className="w-4 h-4" />
                New Product
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center">
              Create a new product
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    if (activeTab === "bundles") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="ml-2 rounded-md"
                onClick={() => {
                  openBundleModal();
                }}
                aria-label="Create a new checkout page"
              >
                <Plus className="w-4 h-4" />
                New Checkout Page
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center">
              Create a new checkout page to sell multiple products together
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  };

 

  return (
    <div
      className={`relative h-auto w-full transition-all duration-300 ${
        isSidebarCollapsed ? "md:pl-20" : "md:pl-60"
      }`}
    >
      <div className="w-full">
        {/* Fixed Header */}
        <div className="px-4">
          <div className="container">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between pt-6 pb-6 ">

               <h1 className="text-3xl font-semibold mb-4 md:mb-0">
                {activeTab === "bundles"
                  ? "Checkout Pages"
                  : activeTab === "products"
                  ? "Product List"
                  : activeTab === "subscriptions"
                  ? "" // "Subscription Management" 
                  : activeTab === "transactions"
                  ? "Transactions"
                  : activeTab === "savings_calculator"
                  ? "Savings Calculator"
                  : ""}
              </h1>

              <div className="flex items-center space-x-2">
                {["products", "bundles"].includes(activeTab) && (
                  <>
                    {isStripeConnected && renderActionButton()}
                  </>
                )}
                {activeTab === "transactions" && isStripeConnected && (
                  <RefreshButton
                    onClick={() => handleTabRefresh("transaction-force-refresh", setIsTransactionsRefreshing)}
                    isRefreshing={isTransactionsRefreshing}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="w-full overflow-y-auto scrollbar-hide px-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="savings_calculator" className="mt-0">
            <SavingsCalculatorTab
              stripeConnected={isStripeConnected}
              onNavigateToTab={setActiveTab}
            />
          </TabsContent>
          <TabsContent value="bundles" className="mt-0">
            <BundlesTab
              filterStatus={offerFilterStatus}
              searchQuery={searchQuery}
              onEdit={handleEditOffer}
            />
          </TabsContent>
          <TabsContent value="products" className="mt-0">
            <ProductsTab
              stripeConnected={isStripeConnected}
              hasCreatedFirstProduct={hasCreatedFirstProduct}
              filterStatus={filterStatus}
              searchQuery={searchQuery}
              productTypeFilter={productTypeFilter}
            />
          </TabsContent>
          {showSubscriptionsTab && activeTab === "subscriptions" && (
            <TabsContent value="subscriptions" className="mt-0">
              <SubscriptionsTab stripeConnected={isStripeConnected} />
            </TabsContent>
          )}
          <TabsContent value="transactions" className="mt-0">
            <TransactionsTab
              filterStatus={filterStatus}
              stripeConnected={isStripeConnected}
              searchQuery={searchQuery}
              showFilterBar={showTransactionFilterBar}
            />
          </TabsContent>
          <TabsContent value="settings" className="mt-0">
            <Settings isSidebarCollapsed={isSidebarCollapsed} />
          </TabsContent>
        </Tabs>
        <Dialog
          open={showProductModal || showCreateProductModal}
          onOpenChange={(open) => {
            if (open === false) {
              closeProductModal();
              setShowCreateProductModal(false);
            }
          }}
        >
          <div className="">
            <DialogContent className="w-[95%] sm:w-[80%] md:w-[60%] lg:w-[40%] xl:w-[25%] p-0 overflow-hidden rounded-none shadow-lg">
              <UnifiedProductForm
                onClose={() => {
                  closeProductModal();
                  setShowCreateProductModal(false);
                }}
              />
            </DialogContent>
          </div>
        </Dialog>
        <Dialog
          open={showBundleModal}
          onOpenChange={closeBundleModal}
        >
          <DialogContent className="w-[95%] sm:w-[80%] md:w-[60%] lg:w-[40%] xl:w-[25%] p-0 rounded-none shadow-lg">
            <div className="">
              <OfferPageForm onClose={closeBundleModal} />
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={showEditOfferModal} onOpenChange={setShowEditOfferModal}>
          <DialogContent className="w-[95%] sm:w-[80%] md:w-[60%] lg:w-[40%] xl:w-[25%] p-0 rounded-none shadow-lg">
            <div className="">
              {editingOfferPage && (
                <EditOfferForm
                  offerPageId={editingOfferPage}
                  onClose={() => {
                    setShowEditOfferModal(false);
                    setEditingOfferPage(null);
                  }}
                  onSuccess={() => {
                    setShowEditOfferModal(false);
                    setEditingOfferPage(null);
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardContent;